import { ZfBox, ZfDialog, ZfDialogContent, ZfDialogFooter, ZfDialogHeader, ZfStack, ZfTextButton } from '@zafire/react';
import lodash from 'lodash';
import moment from 'moment';
import type { FunctionComponent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient, useDeleteAdvisoryNeedTask, useDeleteLifeEvent } from '../../../api';
import type { AdvisoryNeedTaskDto, LifeEventDto, MaturityDto, QualifiedIdentity } from '../../../api/generated/client';
import { useRoadmapPageContext } from '../../../contexts/roadmap-page-context';
import { useActiveEvent } from '../../../hooks/useActiveEvent';
import { useBalanceEntries } from '../../../hooks/useBalanceAccounts';
import type { RoadmapEvent } from '../../../interfaces/event';
import { closestEventAfterDate, isAdvisoryNeedTask, isLifeEvent } from '../../../utils/eventUtils';
import { NeedEventModal } from '../../topic/modals/NeedEventModal';
import { BalanceEntryModalWrapper } from '../../wealth';
import { RoadmapButton } from './RoadmapButton';
import { RoadmapListItem } from './RoadmapListItem';

interface IProps {
    allEvents: RoadmapEvent[];
}

export const RoadmapList: FunctionComponent<IProps> = ({ allEvents }) => {
    const [showPastEvents, setShowPastEvents] = useState<boolean>(false);

    const { showCompletedEvents } = useRoadmapPageContext();

    const isPastEvent = (dueDate: moment.Moment) => dueDate.isBefore(moment().startOf('day'));

    const displayedEvents = useMemo<RoadmapEvent[]>(
        () =>
            allEvents
                .filter((roadmapEvent) => showPastEvents || !isPastEvent(moment(roadmapEvent.dueDate)))
                .filter((roadmapEvent) => showCompletedEvents || ['DEFINED', 'IN_PROGRESS'].includes(roadmapEvent.status!)),
        [allEvents, showPastEvents, showCompletedEvents],
    );

    const eventAfterCurrentDate = useMemo<RoadmapEvent | undefined>(() => closestEventAfterDate(allEvents, moment().startOf('day')), [allEvents]);

    const { data: client } = useClient();
    const balanceEntries = useBalanceEntries();
    const deleteLifeEvent = useDeleteLifeEvent();
    const deleteAdvisoryNeedTask = useDeleteAdvisoryNeedTask();
    const { t } = useTranslation();
    const scrollableContainerRef = useRef(null);
    const [activeEvent, setActiveEvent] = useActiveEvent();
    const [eventToEdit, setEventToEdit] = useState<RoadmapEvent | undefined>(undefined);
    const [eventToDelete, setEventToDelete] = useState<RoadmapEvent | undefined>(undefined);

    useEffect(() => {
        if (activeEvent) {
            setEventToEdit(activeEvent);
        }
    }, [activeEvent]);

    const roadmapItemsGroupedByYear = useMemo(() => {
        const groupedByYear = lodash.groupBy(displayedEvents, (item) => moment(item.dueDate).year());

        return lodash.mapValues(groupedByYear, (events) => lodash.orderBy(events, ['dueDate'], ['asc']));
    }, [displayedEvents]);

    const isLastRoadmapItem = (year: string) => {
        const keys = Object.keys(roadmapItemsGroupedByYear);

        return keys[keys.length - 1] === year;
    };
    const getIconPosition = (year: string, itemIndex: number, groupIndex: number, showCurrentDateDivider: boolean) => {
        if (showCurrentDateDivider || (itemIndex === 0 && groupIndex === 0)) {
            return 'top';
        }

        if (isLastRoadmapItem(year)) {
            return 'bottom';
        } else {
            return 'middle';
        }
    };

    // Edit Event modal
    const showEventModal = (event?: RoadmapEvent): event is RoadmapEvent<AdvisoryNeedTaskDto | LifeEventDto> =>
        event != null && event?.eventType !== 'MATURITY';
    const handleEditClick = (roadmapEvent: RoadmapEvent) => setEventToEdit(roadmapEvent);
    const onCloseEditingModal = () => {
        setEventToEdit(undefined);
        setActiveEvent(undefined);
    };

    // Edit Maturity event
    const showClmWebComponent = () => eventToEdit != null && eventToEdit?.eventType === 'MATURITY';
    const getBalanceEntryById = (qualifiedIdentity: QualifiedIdentity) => balanceEntries.find((entry) => entry.qualifiedKey === qualifiedIdentity.qualifiedKey);

    // Delete event
    const handleDeleteClick = (roadmapEvent: RoadmapEvent) => setEventToDelete(roadmapEvent);
    const handleDelete = () => {
        if (isLifeEvent(eventToDelete) && eventToDelete.id) {
            deleteLifeEvent.mutate(eventToDelete.id);
        }
        if (isAdvisoryNeedTask(eventToDelete)) {
            deleteAdvisoryNeedTask.mutate(eventToDelete.id);
        }
        setEventToDelete(undefined);
    };

    const updateDisplayedEvents = () => setShowPastEvents(!showPastEvents);
    const showCurrentDateDivider = (event: RoadmapEvent) => event === eventAfterCurrentDate && showPastEvents;

    return (
        <ZfBox ref={scrollableContainerRef} style={{ height: '100%', overflow: 'auto', paddingTop: '16px', paddingBottom: '16px', position: 'relative' }}>
            {showEventModal(eventToEdit) && <NeedEventModal event={eventToEdit} onClose={onCloseEditingModal} isOpen={true} />}
            <RoadmapButton showPastEvents={showPastEvents} updateDisplayedEvents={updateDisplayedEvents} />
            <div>
                {Object.entries(roadmapItemsGroupedByYear).map(([year, itemsPerYear], groupIndex) =>
                    itemsPerYear.map((item, itemIndex) => (
                        <RoadmapListItem
                            key={`${groupIndex} ${itemIndex}`}
                            index={`${groupIndex} ${itemIndex}`}
                            item={item}
                            timeValue={itemIndex === 0 ? year : ''}
                            iconPosition={getIconPosition(year, itemIndex, groupIndex, showCurrentDateDivider(item))}
                            showCurrentDateDivider={showCurrentDateDivider(item)}
                            onEditClick={() => handleEditClick(item)}
                            onDeleteClick={() => handleDeleteClick(item)}
                        />
                    )),
                )}
            </div>
            <ZfDialog size="s" show={!!eventToDelete} showCloseButton={true} onDialogClose={() => setEventToDelete(undefined)}>
                <ZfDialogHeader>{t('modalConfirmDeleteTitle')}</ZfDialogHeader>
                <ZfDialogContent>{t('modalConfirmDeleteText')}</ZfDialogContent>
                <ZfDialogFooter>
                    <ZfStack slot="primary" direction="row" spacing="extra-small">
                        <ZfTextButton size="medium" hierarchy="tertiary" onClick={() => setEventToDelete(undefined)}>
                            {t('cancel')}
                        </ZfTextButton>
                        <ZfTextButton size="medium" hierarchy="primary" kind="destructive" onClick={handleDelete}>
                            {t('modalDeleteButton')}
                        </ZfTextButton>
                    </ZfStack>
                </ZfDialogFooter>
            </ZfDialog>
            {showClmWebComponent() && (
                <BalanceEntryModalWrapper
                    woCategoryId={(eventToEdit as MaturityDto).categoryId}
                    partnerUid={client?.partnerUuid}
                    balanceEntry={getBalanceEntryById(eventToEdit as MaturityDto)}
                    onClose={onCloseEditingModal}
                />
            )}
        </ZfBox>
    );
};
